import { apiSlice } from './apiSlice';

const ENDPOINT_URL = process.env.REACT_APP_CUSTOMERS;
const ENDPOINT_BMS = process.env.REACT_APP_BMS_SERVICE;
const ENDPOINT_GEOIP = process.env.REACT_APP_GEOIP_API_URL;
const ENDPOINT_ZENDESK = process.env.REACT_APP_ZENDESK_TICKET_URL;
const ENDPOINT_ZENDESK_ARTICLE = process.env.REACT_APP_ZENDESK_ARTICLE_URL;

const GATEWAY_ACCOUNT_BASE = `${ENDPOINT_URL}/account-details`;

export const gatewayApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getUserSubscriptions: builder.query({
      query: () => ({ url: `${GATEWAY_ACCOUNT_BASE}/user-subscriptions` }),
    }),

    getAllProducts: builder.query({
      query: () => ({ url: `${GATEWAY_ACCOUNT_BASE}/all-products` }),
    }),

    getUserPaymentHistory: builder.query({
      query: (userId) => ({ url: `${GATEWAY_ACCOUNT_BASE}/user-payment-history/${userId}` }),
    }),

    getAccountRuleStatus: builder.query({
      query: (username) => ({
        url: `${ENDPOINT_URL}/service`,
        method: 'POST',
        body: { action: 'get-user-rule-status', params: { username } },
      }),
    }),

    getProductsImages: builder.query({
      query: () => ({
        url: `${ENDPOINT_URL}/service`,
        method: 'POST',
        body: { action: 'get-products-images', params: {} },
      }),
    }),

    getNewsByPath: builder.query({
      query: (aliasPath) => ({
        url: `${ENDPOINT_URL}/service/ecommerce`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: {
          action: 'get-news-path',
          params: {
            aliasPath,
          },
        },
      }),
      transformResponse: (response) => response.data,
    }),

    getEnabledNews: builder.query({
      query: ({ page = 1, pageSize = 20 }) => ({
        url: `${ENDPOINT_URL}/service/ecommerce`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: {
          action: 'get-enabled-news',
          params: {
            skip: (page - 1) * pageSize,
            take: pageSize,
          },
        },
      }),
      transformResponse: (response) => response.data,
    }),

    getPaymentInformationUrl: builder.query({
      query: ({ subscriptionId, idToken }) => ({
        url: `${ENDPOINT_URL}/Processors/payment-information/${subscriptionId}/url`,
        headers: {
          authorization: `Bearer ${idToken}`,
        },
      }),
    }),

    cancelSubscription: builder.mutation({
      query: ({ subscriptionId, idToken }) => ({
        url: `${ENDPOINT_URL}/Processors/subscription/${subscriptionId}`,
        method: 'DELETE',
        headers: {
          authorization: `Bearer ${idToken}`,
        },
      }),
    }),

    sendCancellationSurvey: builder.mutation({
      query: ({
        subscriptionId,
        cancellationReason,
        cancellationComment,
        idToken,
      }) => ({
        url: `${ENDPOINT_URL}/service`,
        method: 'POST',
        headers: {
          authorization: `Bearer ${idToken}`,
        },
        body: {
          action: 'no-renew-subscription',
          params: {
            subscriptionId,
            cancellationReason,
            cancellationComment,
          },
        },
      }),
    }),

    getUserRuleStatus: builder.query({
      query: ({ username, idToken }) => ({
        url: `${ENDPOINT_URL}/api/service`,
        method: 'POST',
        headers: {
          authorization: `Bearer ${idToken}`,
        },
        body: {
          action: 'get-user-rule-status',
          params: {
            username,
          },
        },
      }),
      transformResponse: (response) => response.data,
    }),

    claimReward: builder.mutation({
      query: ({
        username, productId, source, userRuleId, userSubscriptionRuleId, idToken,
      }) => {
        // Callers pass only the ids the rule actually has, already resolved to
        // strings, so anything absent here is simply left out of the payload.
        const data = [{ key: 'ProductId', value: String(productId) }];

        if (userRuleId) {
          data.push({ key: 'UserRuleId', value: userRuleId });
        }
        if (userSubscriptionRuleId) {
          data.push({ key: 'UserSubscriptionRuleId', value: userSubscriptionRuleId });
        }

        const params = { username };
        if (source !== null && source !== undefined) {
          params.source = source;
        }
        params.data = data;

        return {
          url: `${ENDPOINT_URL}/service`,
          method: 'POST',
          headers: {
            authorization: `Bearer ${idToken}`,
          },
          body: {
            action: 'put-user-product',
            params,
          },
        };
      },
    }),

    removeReward: builder.mutation({
      query: ({ username, productId, idToken }) => ({
        url: `${ENDPOINT_URL}/service`,
        method: 'POST',
        headers: {
          authorization: `Bearer ${idToken}`,
        },
        body: {
          action: 'delete-user-product',
          params: {
            username,
            id: String(productId),
          },
        },
      }),
    }),

    getHomePage: builder.query({
      query: () => ({
        url: `${ENDPOINT_BMS}/HomePage`,
        method: 'GET',
      }),
      transformResponse: (response) => response.data,
    }),

    getDownloadPage: builder.query({
      query: () => ({
        url: `${ENDPOINT_BMS}/HomePage/download-page`,
        method: 'GET',
      }),
      transformResponse: (response) => response.data,
    }),

    getContactPage: builder.query({
      query: () => ({
        url: `${ENDPOINT_BMS}/HomePage/contact-page`,
        method: 'GET',
      }),
      transformResponse: (response) => response.data,
    }),

    createZendeskTicket: builder.mutation({
      query: ({ payload, idToken }) => ({
        url: `${ENDPOINT_ZENDESK}/zendesk/request`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'authorization': `Bearer ${idToken}`,
        },
        body: payload,
      }),
    }),

    getCountryName: builder.query({
      query: () => ({
        url: `${ENDPOINT_GEOIP}/geoip`,
        method: 'GET',
      }),
      transformResponse: (response) => response?.country?.names?.en || 'United States',
      transformErrorResponse: () => 'United States',
    }),

    getPopularArticles: builder.query({
      query: ({
        query = 'roland cloud',
        perPage = 100,
      } = {}) => ({
        url: `${ENDPOINT_ZENDESK_ARTICLE}/api/v2/help_center/articles/search.json`,
        method: 'GET',
        params: {
          locale: 'en-us',
          query,
          per_page: perPage,
        },
      }),
      transformResponse: (response) => ({
        ...response,
        results: [...response.results].sort(
          (a, b) => (b.vote_sum || 0) - (a.vote_sum || 0),
        ),
      }),
    }),
  }),
});

export const {
  useGetUserSubscriptionsQuery,
  useGetAllProductsQuery,
  useGetUserPaymentHistoryQuery,
  useGetAccountRuleStatusQuery,
  useGetProductsImagesQuery,
  useLazyGetPaymentInformationUrlQuery,
  useGetNewsByPathQuery,
  useGetEnabledNewsQuery,
  useCancelSubscriptionMutation,
  useSendCancellationSurveyMutation,
  useGetUserRuleStatusQuery,
  useClaimRewardMutation,
  useRemoveRewardMutation,
  useGetHomePageQuery,
  useGetDownloadPageQuery,
  useGetContactPageQuery,
  useCreateZendeskTicketMutation,
  useLazyGetCountryNameQuery,
  useGetPopularArticlesQuery,
} = gatewayApiSlice;
